import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import type { ReferenceMaterial } from './useContentReferenceMaterials';

const columnHelper = createColumnHelper<ReferenceMaterial>();

const linkClass = 'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover tw-no-underline tw-transition-colors';

const ImageCell = ({ src }: { src: string | null }) => {
  if (!src) return <span className="tw-text-spa-tertiary tw-text-xs">No image</span>;

  return (
    <img
      alt=""
      className="tw-w-10 tw-h-10 tw-object-cover tw-rounded"
      src={src}
    />
  );
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);

  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

// AIDEV-NOTE: Column definitions for the Reference Materials @tanstack/react-table.
// Matches legacy admin/ReferenceMaterialsTable columns minus the bulk-select checkbox.
const getReferenceMaterialColumns = (): ColumnDef<ReferenceMaterial, unknown>[] => [
  columnHelper.display({
    cell: info => <ImageCell src={info.row.original.primaryImage} />,
    enableSorting: false,
    header: 'Image',
    id: 'image',
    size: 80,
  }),
  columnHelper.accessor('title', {
    cell: info => (
      <a
        className={linkClass}
        href={Routes.edit_admin_reference_material_path(info.row.original.id)}
      >
        {info.getValue()}
      </a>
    ),
    header: 'Title',
    size: 300,
  }),
  columnHelper.accessor('questionsCount', {
    cell: info => (
      <a
        className={linkClass}
        href={Routes.edit_questions_admin_reference_material_path(info.row.original.id)}
      >
        {info.getValue()}
      </a>
    ),
    header: 'Questions',
    size: 80,
  }),
  columnHelper.accessor('programSummary', {
    header: 'Programs',
    size: 200,
  }),
  columnHelper.accessor('nonStandardsTags', {
    header: 'Tags',
    size: 150,
  }),
  columnHelper.accessor('published', {
    cell: info => <span>{info.getValue() ? 'Yes' : 'No'}</span>,
    header: 'Published',
    size: 80,
  }),
  columnHelper.accessor('createdAt', {
    cell: info => <span>{formatDate(info.getValue())}</span>,
    header: 'Created',
    size: 110,
  }),
  columnHelper.accessor('updatedAt', {
    cell: info => <span>{formatDate(info.getValue())}</span>,
    header: 'Updated',
    size: 110,
  }),
  columnHelper.display({
    cell: info => (
      <a
        className={cn(
          'tw-inline-block tw-px-3 tw-py-1 tw-text-xs tw-rounded tw-no-underline tw-transition-colors',
          'tw-border tw-border-solid tw-border-spa-accent tw-text-spa-accent-text',
          'hover:tw-bg-spa-accent-bg-subtle hover:tw-text-spa-accent-text-hover',
        )}
        href={Routes.edit_admin_reference_material_path(info.row.original.id)}
      >
        Edit
      </a>
    ),
    enableSorting: false,
    header: 'Actions',
    id: 'actions',
    size: 70,
  }),
];

export default getReferenceMaterialColumns;
